import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  Check,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Scale,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import NavMemoire, { type OngletMemoire } from '../../components/memoire/NavMemoire';
import RenduDocument from '../../components/assistants/RenduDocument';
import ToastContainer from '../../components/ui/ToastContainer';
import Competences from '../../components/memoire/Competences';
import Modeles from '../../components/memoire/Modeles';
import { useToast } from '../../hooks/useToast';
import { authManager } from '../../services/authManager';
import {
  type ApercuMemoire,
  type ConflitMemoire,
  type DocumentMemoire,
  type FaitMemoire,
  type FicheMemoire,
  type Fraicheur,
  type SourceAutorite,
  memoireService,
} from '../../services/api/memoire';

// ------------------------------------------------------------------ Commun

const Carte: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07] ${className}`}>
    {children}
  </div>
);

const Pastille: React.FC<{ children: React.ReactNode; ton?: 'neutre' | 'sombre' | 'accent' | 'alerte' }> = ({
  children, ton = 'neutre',
}) => {
  const tons = {
    neutre: 'bg-black/[0.045] text-eg-brun',
    sombre: 'bg-eg-ombre text-white',
    accent: 'bg-eg-accent/[0.12] text-eg-accent',
    alerte: 'bg-eg-alerte/[0.12] text-eg-alerte',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-medium tracking-tight ${tons[ton]}`}>
      {children}
    </span>
  );
};

const FRAICHEUR: Record<Fraicheur, { libelle: string; classe: string }> = {
  chaud: { libelle: 'Récent', classe: 'bg-eg-valide' },
  tiede: { libelle: 'Quelques mois', classe: 'bg-eg-alerte' },
  froid: { libelle: 'Ancien', classe: 'bg-eg-estompe' },
};

const PointFraicheur: React.FC<{ fraicheur: Fraicheur }> = ({ fraicheur }) => (
  <span title={FRAICHEUR[fraicheur].libelle} className={`inline-block h-2 w-2 shrink-0 rounded-full ${FRAICHEUR[fraicheur].classe}`} />
);

const dateLisible = (valeur: string | null) =>
  valeur ? new Date(valeur).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const BoutonSombre: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center gap-1.5 rounded-full bg-eg-ombre px-3.5 py-1.5 text-[12.5px] font-medium
      text-white hover:bg-eg-profond disabled:opacity-40 ${className}`}
  />
);

const BoutonClair: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ className = '', ...props }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center gap-1.5 rounded-full bg-black/[0.045] px-3.5 py-1.5 text-[12.5px] font-medium
      text-eg-brun hover:bg-black/[0.07] disabled:opacity-40 ${className}`}
  />
);

const champ = 'w-full rounded-xl border border-eg-bord bg-eg-blanc px-3 py-2 text-[13px] text-eg-encre placeholder:text-eg-estompe focus:border-eg-accent focus:outline-none focus:ring-4 focus:ring-eg-accent/10';

// ------------------------------------------------------------------ Aperçu

const Apercu: React.FC<{ apercu: ApercuMemoire | null }> = ({ apercu }) => {
  if (!apercu) return null;
  const total = apercu.fraicheur.chaud + apercu.fraicheur.tiede + apercu.fraicheur.froid;
  const part = Math.min(100, Math.round((apercu.occupation.caracteres / apercu.occupation.budget) * 100));
  const chiffres = [
    { libelle: 'Documents de référence', valeur: apercu.documents, detail: `${apercu.documents_total} versés` },
    { libelle: 'Fiches', valeur: apercu.fiches, detail: 'lues dans chaque échange' },
    { libelle: 'Faits retenus', valeur: apercu.faits, detail: `${apercu.faits_remplaces} valeurs écartées` },
    { libelle: 'À trancher', valeur: apercu.conflits_ouverts, detail: 'désaccords de même rang' },
  ];
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {chiffres.map((c) => (
          <Carte key={c.libelle} className="p-4">
            <div className="text-[12px] text-eg-gris">{c.libelle}</div>
            <div className="mt-1 text-[26px] font-semibold tracking-tight text-eg-encre">{c.valeur}</div>
            <div className="text-[11.5px] text-eg-estompe">{c.detail}</div>
          </Carte>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Carte className="p-5">
          <h3 className="text-[14px] font-medium text-eg-encre">Comment la mémoire travaille</h3>
          <ol className="mt-3 space-y-3 text-[13px] leading-relaxed text-eg-brun">
            <li><span className="font-medium text-eg-encre">1. On verse un document</span> et on dit de quelle nature il est : directive du Ministère, rapport validé, plan, compte rendu…</li>
            <li><span className="font-medium text-eg-encre">2. Les faits en sont relevés</span>, chacun avec le passage exact qui l'établit. Un fait sans passage retrouvé dans le document est écarté.</li>
            <li><span className="font-medium text-eg-encre">3. Les désaccords se règlent seuls</span> par la hiérarchie des sources. Seuls deux documents de même rang qui se contredisent demandent qu'un responsable tranche.</li>
          </ol>
        </Carte>
        <Carte className="p-5">
          <h3 className="text-[14px] font-medium text-eg-encre">Fraîcheur des faits retenus</h3>
          <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-black/[0.05]">
            {total > 0 && (['chaud', 'tiede', 'froid'] as Fraicheur[]).map((f) => (
              <span key={f} className={FRAICHEUR[f].classe} style={{ width: `${(apercu.fraicheur[f] / total) * 100}%` }} />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-eg-gris">
            {(['chaud', 'tiede', 'froid'] as Fraicheur[]).map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5">
                <PointFraicheur fraicheur={f} /> {FRAICHEUR[f].libelle} : {apercu.fraicheur[f]}
              </span>
            ))}
          </div>
          <h3 className="mt-6 text-[14px] font-medium text-eg-encre">Place occupée dans chaque échange</h3>
          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-black/[0.05]">
            <span className={`block h-full ${part > 90 ? 'bg-eg-alerte' : 'bg-eg-accent'}`} style={{ width: `${part}%` }} />
          </div>
          <p className="mt-2 text-[12px] text-eg-gris">
            {part} % du budget de lecture. Au-delà, les documents les moins fiables sont seulement
            nommés aux assistants, pas lus en entier.
          </p>
        </Carte>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ Documents

const Documents: React.FC<{
  sources: SourceAutorite[];
  estAdmin: boolean;
  onChange: () => void;
  notifier: ReturnType<typeof useToast>;
}> = ({ sources, estAdmin, onChange, notifier }) => {
  const [documents, setDocuments] = useState<DocumentMemoire[] | null>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [nom, setNom] = useState('');
  const [typeSource, setTypeSource] = useState('directive');
  const [dateDocument, setDateDocument] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [extractions, setExtractions] = useState<Record<number, string>>({});
  const [aSupprimer, setASupprimer] = useState<number | null>(null);
  const [texte, setTexte] = useState<{ nom: string; contenu: string } | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);

  const charger = useCallback(() => memoireService.documents().then(setDocuments), []);
  useEffect(() => { charger().catch(() => setDocuments([])); }, [charger]);

  const verser = async () => {
    if (!fichier) return;
    setEnvoi(true);
    try {
      await memoireService.verserDocument(fichier, {
        nom: nom.trim() || undefined, type_source: typeSource, date_document: dateDocument || undefined,
      });
      setFichier(null); setNom(''); setDateDocument('');
      if (champFichier.current) champFichier.current.value = '';
      await charger();
      onChange();
      notifier.showInfo('Document versé', 'Il est lu par les assistants dès maintenant ; ses faits sont relevés en tâche de fond.');
    } catch (e: any) {
      notifier.showError('Versement impossible', e.message);
    } finally {
      setEnvoi(false);
    }
  };

  const extraire = async (id: number) => {
    setExtractions((x) => ({ ...x, [id]: 'Mise en file' }));
    try {
      const { tache } = await memoireService.extraireFaits(id);
      const suivre = async () => {
        const etat = await memoireService.suivreTache(tache);
        if (etat.etat === 'terminee') {
          setExtractions((x) => { const y = { ...x }; delete y[id]; return y; });
          notifier.showInfo('Faits relevés', `${etat.resultat?.retenus ?? 0} retenus, ${etat.resultat?.ecartes ?? 0} écartés faute de passage à l'appui.`);
          await charger(); onChange();
          return;
        }
        if (etat.etat === 'echouee') {
          setExtractions((x) => { const y = { ...x }; delete y[id]; return y; });
          notifier.showError('Relevé impossible', etat.erreur || 'Le relevé a échoué.');
          return;
        }
        setExtractions((x) => ({ ...x, [id]: etat.avancement || 'En cours' }));
        setTimeout(suivre, 4000);
      };
      setTimeout(suivre, 2000);
    } catch (e: any) {
      setExtractions((x) => { const y = { ...x }; delete y[id]; return y; });
      notifier.showError('Relevé impossible', e.message);
    }
  };

  const [toutEnCours, setToutEnCours] = useState(false);
  const sansFaits = (documents || []).filter((d) => d.actif && !d.faits_extraits_le).length;

  const extraireTout = async () => {
    setToutEnCours(true);
    try {
      const { documents: nombre } = await memoireService.extraireTout();
      notifier.showInfo(
        'Relevé lancé',
        `${nombre} document${nombre > 1 ? 's' : ''} en file. Comptez une à deux minutes par document ; la liste se met à jour d'elle-même.`,
      );
      const rafraichir = (restant: number) => {
        if (restant <= 0) { setToutEnCours(false); return; }
        setTimeout(async () => { await charger(); onChange(); rafraichir(restant - 1); }, 30000);
      };
      rafraichir(Math.max(4, nombre * 4));
    } catch (e: any) {
      setToutEnCours(false);
      notifier.showError('Relevé impossible', e.message);
    }
  };

  const modifier = async (document: DocumentMemoire, champs: Partial<DocumentMemoire>) => {
    try {
      await memoireService.modifierDocument(document.id, champs as any);
      await charger(); onChange();
    } catch (e: any) {
      notifier.showError('Modification impossible', e.message);
    }
  };

  const supprimer = async (id: number) => {
    try {
      await memoireService.supprimerDocument(id);
      setASupprimer(null);
      await charger(); onChange();
    } catch (e: any) {
      notifier.showError('Suppression impossible', e.message);
    }
  };

  return (
    <div className="space-y-5">
      {estAdmin && (
        <Carte className="p-5">
          <h3 className="text-[14px] font-medium text-eg-encre">Verser un document de référence</h3>
          <p className="mt-1 text-[12.5px] text-eg-gris">
            Word, PDF, Excel ou texte. Sa nature décide de son autorité quand il contredit une autre pièce.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <input
              ref={champFichier}
              type="file"
              accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv"
              onChange={(e) => setFichier(e.target.files?.[0] || null)}
              className={`${champ} file:mr-3 file:rounded-full file:border-0 file:bg-eg-voile file:px-3 file:py-1 file:text-[12px]`}
            />
            <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Intitulé (par défaut, le nom du fichier)" className={champ} />
            <select value={typeSource} onChange={(e) => setTypeSource(e.target.value)} className={champ}>
              {sources.filter((s) => s.type_source !== 'correction' && s.type_source !== 'dhis2').map((s) => (
                <option key={s.type_source} value={s.type_source}>{s.rang}. {s.libelle}</option>
              ))}
            </select>
            <input type="date" value={dateDocument} onChange={(e) => setDateDocument(e.target.value)} className={champ} title="Date du document" />
          </div>
          <div className="mt-4 flex justify-end">
            <BoutonSombre onClick={verser} disabled={!fichier || envoi}>
              {envoi ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              Verser à la mémoire
            </BoutonSombre>
          </div>
        </Carte>
      )}

      {estAdmin && sansFaits > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#FFF6E0] px-4 py-3 text-[13px] text-[#7A5200]">
          <span>
            {sansFaits} document{sansFaits > 1 ? 's' : ''} n'{sansFaits > 1 ? 'ont' : 'a'} jamais été relevé{sansFaits > 1 ? 's' : ''} :
            les assistants les lisent, mais leurs faits ne sont ni arbitrés ni visibles dans l'onglet Faits.
          </span>
          <BoutonSombre onClick={extraireTout} disabled={toutEnCours}>
            {toutEnCours ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {toutEnCours ? 'Relevé en cours…' : 'Relever les faits de tous'}
          </BoutonSombre>
        </div>
      )}

      {documents === null ? (
        <div className="flex justify-center py-10 text-eg-gris"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : documents.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-10 text-center text-[13px] text-eg-estompe">
          Aucun document de référence pour le moment.
        </p>
      ) : (
        <Carte className="overflow-hidden">
          {documents.map((document, index) => (
            <div key={document.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-eg-bordure' : ''} ${document.actif ? '' : 'opacity-60'}`}>
              <FileText className="h-4 w-4 shrink-0 text-eg-estompe" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-medium text-eg-encre">{document.nom}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-eg-gris">
                  {estAdmin ? (
                    <select
                      value={document.type_source}
                      onChange={(e) => modifier(document, { type_source: e.target.value })}
                      className="rounded-full border-0 bg-black/[0.045] py-0.5 pl-2 pr-7 text-[11px] font-medium text-eg-brun focus:ring-2 focus:ring-eg-accent/20"
                    >
                      {sources.map((s) => <option key={s.type_source} value={s.type_source}>{s.libelle}</option>)}
                    </select>
                  ) : (
                    <Pastille>{document.type_source_libelle}</Pastille>
                  )}
                  <span>{dateLisible(document.date_document)}</span>
                  <span>· {document.faits ?? 0} fait{(document.faits ?? 0) > 1 ? 's' : ''}</span>
                  <span>· versé par {document.depose_par || '—'}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <BoutonClair onClick={() => memoireService.texteDocument(document.id).then(setTexte)}>Lire</BoutonClair>
                {estAdmin && (
                  extractions[document.id] ? (
                    <span className="inline-flex items-center gap-1.5 px-2 text-[12px] text-eg-accent">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> {extractions[document.id]}
                    </span>
                  ) : (
                    <BoutonClair onClick={() => extraire(document.id)}>
                      <Sparkles className="h-3.5 w-3.5" />
                      {document.faits_extraits_le ? 'Relever à nouveau' : 'Relever les faits'}
                    </BoutonClair>
                  )
                )}
                {estAdmin && (
                  <BoutonClair onClick={() => modifier(document, { actif: !document.actif })}>
                    {document.actif ? 'Retirer de la lecture' : 'Remettre en lecture'}
                  </BoutonClair>
                )}
                {estAdmin && (aSupprimer === document.id ? (
                  <span className="inline-flex items-center gap-1.5 text-[12px]">
                    Supprimer, avec ses faits ?
                    <button type="button" onClick={() => supprimer(document.id)} className="font-medium text-[#A33A2B] hover:underline">Oui</button>
                    <button type="button" onClick={() => setASupprimer(null)} className="text-eg-gris hover:underline">Non</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setASupprimer(document.id)} className="rounded-full p-1.5 text-eg-estompe hover:bg-black/[0.05] hover:text-[#A33A2B]" aria-label="Supprimer">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </Carte>
      )}

      {texte && (
        <div className="fixed inset-0 z-50 flex justify-end bg-eg-encre/25" onClick={() => setTexte(null)}>
          <aside onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-[720px] flex-col bg-eg-fond shadow-2xl">
            <div className="flex items-center gap-3 border-b border-eg-bordure px-6 py-4">
              <h2 className="min-w-0 flex-1 truncate text-[16px] font-semibold text-eg-encre">{texte.nom}</h2>
              <button type="button" onClick={() => setTexte(null)} className="rounded-full p-1.5 text-eg-estompe hover:bg-black/[0.05]"><X className="h-4 w-4" /></button>
            </div>
            <pre className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap px-6 py-5 font-sans text-[12.5px] leading-relaxed text-eg-brun">{texte.contenu}</pre>
          </aside>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ Fiches

const Fiches: React.FC<{ estAdmin: boolean; onChange: () => void; notifier: ReturnType<typeof useToast> }> = ({
  estAdmin, onChange, notifier,
}) => {
  const [fiches, setFiches] = useState<FicheMemoire[] | null>(null);
  const [ouverte, setOuverte] = useState<FicheMemoire | null>(null);
  const [edition, setEdition] = useState<{ titre: string; contenu: string } | null>(null);
  const [enregistrement, setEnregistrement] = useState(false);

  const charger = useCallback(() => memoireService.fiches().then(setFiches), []);
  useEffect(() => { charger().catch(() => setFiches([])); }, [charger]);

  const ouvrir = async (fiche: FicheMemoire) => {
    setEdition(null);
    setOuverte(await memoireService.fiche(fiche.id));
  };

  const enregistrer = async () => {
    if (!edition || !edition.titre.trim()) return;
    setEnregistrement(true);
    try {
      const fiche = ouverte
        ? await memoireService.modifierFiche(ouverte.id, edition.titre, edition.contenu)
        : await memoireService.creerFiche(edition.titre, edition.contenu);
      setOuverte(fiche);
      setEdition(null);
      await charger(); onChange();
    } catch (e: any) {
      notifier.showError('Enregistrement impossible', e.message);
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <div className="space-y-2">
        {estAdmin && (
          <BoutonSombre className="w-full justify-center" onClick={() => { setOuverte(null); setEdition({ titre: '', contenu: '' }); }}>
            <Plus className="h-3.5 w-3.5" /> Nouvelle fiche
          </BoutonSombre>
        )}
        {fiches === null ? (
          <div className="flex justify-center py-6 text-eg-gris"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : fiches.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-8 text-center text-[12.5px] text-eg-estompe">
            Aucune fiche. Une fiche dit l'essentiel d'un sujet en une page : calendrier vaccinal, organigramme, règles de calcul…
          </p>
        ) : (
          <Carte className="overflow-hidden">
            {fiches.map((fiche, index) => (
              <button
                key={fiche.id}
                type="button"
                onClick={() => ouvrir(fiche)}
                className={`block w-full px-4 py-2.5 text-left hover:bg-eg-papier ${index > 0 ? 'border-t border-eg-bordure' : ''} ${ouverte?.id === fiche.id ? 'bg-eg-papier' : ''}`}
              >
                <div className="truncate text-[13px] font-medium text-eg-encre">{fiche.titre}</div>
                <div className="text-[11px] text-eg-estompe">v{fiche.version} · {dateLisible(fiche.maj_le)}</div>
              </button>
            ))}
          </Carte>
        )}
      </div>

      <Carte className="min-h-[360px] p-5">
        {edition ? (
          <div className="space-y-3">
            <input value={edition.titre} onChange={(e) => setEdition({ ...edition, titre: e.target.value })} placeholder="Titre de la fiche" className={`${champ} text-[15px] font-medium`} />
            <textarea
              value={edition.contenu}
              onChange={(e) => setEdition({ ...edition, contenu: e.target.value })}
              rows={18}
              placeholder={'Écrivez en Markdown : ## titres, listes à puces, tableaux.\nRestez court : la fiche est lue dans chaque échange.'}
              className={`${champ} font-mono text-[12.5px] leading-relaxed`}
            />
            <div className="flex justify-end gap-2">
              <BoutonClair onClick={() => setEdition(null)}>Annuler</BoutonClair>
              <BoutonSombre onClick={enregistrer} disabled={enregistrement || !edition.titre.trim()}>
                {enregistrement ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Enregistrer
              </BoutonSombre>
            </div>
          </div>
        ) : ouverte ? (
          <div>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-[18px] font-semibold tracking-tight text-eg-encre">{ouverte.titre}</h3>
                <div className="mt-0.5 text-[11.5px] text-eg-estompe">
                  Version {ouverte.version} · {ouverte.maj_par || '—'} · {dateLisible(ouverte.maj_le)}
                </div>
              </div>
              {estAdmin && (
                <BoutonClair onClick={() => setEdition({ titre: ouverte.titre, contenu: ouverte.contenu })}>
                  <Pencil className="h-3.5 w-3.5" /> Modifier
                </BoutonClair>
              )}
            </div>
            <div className="mt-4 text-[13px]"><RenduDocument contenu={ouverte.contenu || '_Fiche vide._'} /></div>
            {estAdmin && (ouverte.versions || []).length > 0 && (
              <div className="mt-6 border-t border-eg-bordure pt-4">
                <h4 className="text-[12px] font-medium text-eg-gris">Versions précédentes</h4>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(ouverte.versions || []).map((v) => (
                    <button
                      key={v.version}
                      type="button"
                      onClick={() => setEdition({ titre: v.titre, contenu: v.contenu })}
                      title="Reprendre cette version dans l'éditeur"
                      className="rounded-full bg-black/[0.045] px-2.5 py-1 text-[11.5px] text-eg-brun hover:bg-black/[0.07]"
                    >
                      v{v.version} · {dateLisible(v.maj_le)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 text-center text-eg-estompe">
            <BookOpen className="h-6 w-6" />
            <p className="text-[13px]">Choisissez une fiche.</p>
          </div>
        )}
      </Carte>
    </div>
  );
};

// ------------------------------------------------------------------ Faits

const LigneFait: React.FC<{ fait: FaitMemoire; onOuvrir?: () => void }> = ({ fait, onOuvrir }) => (
  <button type="button" onClick={onOuvrir} className="grid w-full grid-cols-[1fr_auto] items-center gap-3 px-4 py-2.5 text-left hover:bg-eg-papier">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <PointFraicheur fraicheur={fait.fraicheur} />
        <span className="truncate text-[13px] text-eg-brun">
          {fait.attribut}{fait.periode ? ` (${fait.periode})` : ''}
        </span>
      </div>
      <div className="mt-0.5 truncate pl-4 text-[11.5px] text-eg-estompe">
        {fait.type_source_libelle} · {fait.source || 'sans pièce'} · {dateLisible(fait.affirme_le)}
      </div>
    </div>
    <span className={`max-w-[260px] truncate text-right text-[13.5px] font-medium ${fait.statut === 'actif' ? 'text-eg-encre' : 'text-eg-estompe line-through'}`}>
      {fait.valeur}
    </span>
  </button>
);

const Faits: React.FC<{ estAdmin: boolean; onChange: () => void; notifier: ReturnType<typeof useToast> }> = ({
  estAdmin, onChange, notifier,
}) => {
  const [faits, setFaits] = useState<FaitMemoire[] | null>(null);
  const [recherche, setRecherche] = useState('');
  const [tous, setTous] = useState(false);
  const [historique, setHistorique] = useState<FaitMemoire[] | null>(null);
  const [correction, setCorrection] = useState<null | { entite: string; genre: string; attribut: string; valeur: string; periode: string; justification: string }>(null);

  const charger = useCallback(
    () => memoireService.faits({ q: recherche.trim() || undefined, tous }).then(setFaits),
    [recherche, tous],
  );
  useEffect(() => {
    const minuteur = setTimeout(() => { charger().catch(() => setFaits([])); }, 250);
    return () => clearTimeout(minuteur);
  }, [charger]);

  const parEntite = useMemo(() => {
    const groupes: Record<string, FaitMemoire[]> = {};
    (faits || []).forEach((f) => { (groupes[f.entite] = groupes[f.entite] || []).push(f); });
    return Object.entries(groupes);
  }, [faits]);

  const corriger = async () => {
    if (!correction) return;
    try {
      await memoireService.corriger(correction);
      setCorrection(null);
      await charger(); onChange();
      notifier.showInfo('Correction enregistrée', 'Elle prime désormais sur toutes les sources sauf les textes officiels.');
    } catch (e: any) {
      notifier.showError('Correction impossible', e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-eg-estompe" />
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="District, indicateur, valeur, document…" className={`${champ} rounded-full pl-8`} />
        </div>
        <label className="inline-flex items-center gap-2 text-[12.5px] text-eg-gris">
          <input type="checkbox" checked={tous} onChange={(e) => setTous(e.target.checked)} className="rounded border-eg-bord text-eg-accent focus:ring-eg-accent/30" />
          Montrer les valeurs écartées
        </label>
        {estAdmin && (
          <BoutonSombre onClick={() => setCorrection({ entite: '', genre: 'district', attribut: '', valeur: '', periode: '', justification: '' })}>
            <Pencil className="h-3.5 w-3.5" /> Corriger une valeur
          </BoutonSombre>
        )}
      </div>

      {correction && (
        <Carte className="p-5">
          <h3 className="text-[14px] font-medium text-eg-encre">Corriger une valeur</h3>
          <p className="mt-1 text-[12.5px] text-eg-gris">
            Une correction prime sur toutes les sources, sauf les textes officiels du Ministère. Elle est signée de votre nom.
          </p>
          <div className="mt-3 grid gap-2.5 md:grid-cols-3">
            <input value={correction.entite} onChange={(e) => setCorrection({ ...correction, entite: e.target.value })} placeholder="Entité : Siguiri, Penta 3, le programme…" className={champ} />
            <select value={correction.genre} onChange={(e) => setCorrection({ ...correction, genre: e.target.value })} className={champ}>
              {[['programme', 'Programme'], ['region', 'Région'], ['district', 'District / préfecture'], ['antigene', 'Antigène'], ['structure', 'Structure'], ['partenaire', 'Partenaire'], ['autre', 'Autre']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input value={correction.periode} onChange={(e) => setCorrection({ ...correction, periode: e.target.value })} placeholder="Période (facultatif) : 2026, T2 2026…" className={champ} />
            <input value={correction.attribut} onChange={(e) => setCorrection({ ...correction, attribut: e.target.value })} placeholder="Attribut : cible des moins d'un an…" className={champ} />
            <input value={correction.valeur} onChange={(e) => setCorrection({ ...correction, valeur: e.target.value })} placeholder="Valeur" className={champ} />
            <input value={correction.justification} onChange={(e) => setCorrection({ ...correction, justification: e.target.value })} placeholder="Justification" className={champ} />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <BoutonClair onClick={() => setCorrection(null)}>Annuler</BoutonClair>
            <BoutonSombre onClick={corriger} disabled={!correction.entite.trim() || !correction.attribut.trim() || !correction.valeur.trim()}>
              <Check className="h-3.5 w-3.5" /> Enregistrer
            </BoutonSombre>
          </div>
        </Carte>
      )}

      {faits === null ? (
        <div className="flex justify-center py-10 text-eg-gris"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : parEntite.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-10 text-center text-[13px] text-eg-estompe">
          {recherche ? 'Aucun fait ne correspond.' : 'Aucun fait relevé. Versez un document puis relevez-en les faits.'}
        </p>
      ) : (
        <div className="space-y-4">
          {parEntite.map(([entite, liste]) => (
            <div key={entite}>
              <div className="mb-1.5 flex items-center gap-2 px-1">
                <span className="text-[13px] font-medium text-eg-encre">{entite}</span>
                <span className="text-[11.5px] text-eg-estompe">{liste.length}</span>
              </div>
              <Carte className="divide-y divide-eg-bordure overflow-hidden">
                {liste.map((fait) => (
                  <LigneFait key={fait.id} fait={fait} onOuvrir={() => memoireService.historiqueFait(fait.id).then(setHistorique)} />
                ))}
              </Carte>
            </div>
          ))}
        </div>
      )}

      {historique && (
        <div className="fixed inset-0 z-50 flex justify-end bg-eg-encre/25" onClick={() => setHistorique(null)}>
          <aside onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-[560px] flex-col bg-eg-fond shadow-2xl">
            <div className="flex items-start gap-3 border-b border-eg-bordure px-6 py-4">
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-eg-estompe">{historique[0]?.entite}</div>
                <h2 className="text-[16px] font-semibold text-eg-encre">
                  {historique[0]?.attribut}{historique[0]?.periode ? ` (${historique[0].periode})` : ''}
                </h2>
              </div>
              <button type="button" onClick={() => setHistorique(null)} className="rounded-full p-1.5 text-eg-estompe hover:bg-black/[0.05]"><X className="h-4 w-4" /></button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-5">
              <p className="text-[12.5px] text-eg-gris">Toutes les valeurs affirmées, de la retenue aux écartées.</p>
              {historique.map((fait) => (
                <Carte key={fait.id} className="p-4">
                  <div className="flex items-center gap-2">
                    {fait.statut === 'actif' ? <Pastille ton="accent"><Check className="h-3 w-3" />Retenue</Pastille> : <Pastille>Écartée</Pastille>}
                    <span className="ml-auto text-[15px] font-semibold text-eg-encre">{fait.valeur}</span>
                  </div>
                  <div className="mt-2 text-[12px] text-eg-gris">
                    {fait.type_source_libelle} · {fait.source || 'sans pièce'} · {dateLisible(fait.affirme_le)}
                  </div>
                  {fait.extrait && (
                    <blockquote className="mt-2 border-l-2 border-eg-bord pl-3 text-[12.5px] italic text-eg-brun">« {fait.extrait} »</blockquote>
                  )}
                </Carte>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ Conflits

const Conflits: React.FC<{ estAdmin: boolean; onChange: () => void; notifier: ReturnType<typeof useToast> }> = ({
  estAdmin, onChange, notifier,
}) => {
  const [statut, setStatut] = useState<'ouvert' | 'tranche'>('ouvert');
  const [conflits, setConflits] = useState<ConflitMemoire[] | null>(null);

  const charger = useCallback(() => memoireService.conflits(statut).then(setConflits), [statut]);
  useEffect(() => { setConflits(null); charger().catch(() => setConflits([])); }, [charger]);

  const trancher = async (conflit: ConflitMemoire, gagnant: number) => {
    try {
      await memoireService.trancher(conflit.id, gagnant);
      await charger(); onChange();
    } catch (e: any) {
      notifier.showError('Décision impossible', e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1">
        {(['ouvert', 'tranche'] as const).map((s) => (
          <button key={s} type="button" onClick={() => setStatut(s)}
            className={`rounded-full px-3 py-1.5 text-[12.5px] font-medium ${statut === s ? 'bg-black/[0.07] text-eg-encre' : 'text-eg-gris hover:text-eg-encre'}`}>
            {s === 'ouvert' ? 'À trancher' : 'Tranchés'}
          </button>
        ))}
      </div>
      <p className="text-[12.5px] text-eg-gris">
        Deux sources de même rang disent deux choses différentes : la hiérarchie ne peut pas départager. En attendant, la plus récente est retenue.
      </p>
      {conflits === null ? (
        <div className="flex justify-center py-10 text-eg-gris"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : conflits.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-eg-bord px-4 py-10 text-center text-[13px] text-eg-estompe">
          {statut === 'ouvert' ? 'Rien à trancher. Les sources sont d\'accord, ou la hiérarchie les départage.' : 'Aucune décision prise pour le moment.'}
        </p>
      ) : conflits.map((conflit) => (
        <Carte key={conflit.id} className="p-5">
          <div className="flex flex-wrap items-center gap-2">
            <Scale className="h-4 w-4 text-eg-alerte" />
            <span className="text-[14px] font-medium text-eg-encre">{conflit.entite} — {conflit.attribut}{conflit.periode ? ` (${conflit.periode})` : ''}</span>
            {conflit.statut === 'tranche' && (
              <span className="text-[11.5px] text-eg-estompe">tranché par {conflit.tranche_par} le {dateLisible(conflit.tranche_le)}</span>
            )}
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {conflit.faits.map((fait) => (
              <div key={fait.id} className={`rounded-xl px-4 py-3 outline outline-1 -outline-offset-1 ${conflit.gagnant === fait.id ? 'bg-eg-accent/[0.06] outline-eg-accent/40' : 'bg-eg-papier outline-black/[0.05]'}`}>
                <div className="text-[18px] font-semibold tracking-tight text-eg-encre">{fait.valeur}</div>
                <div className="mt-1 text-[12px] text-eg-gris">{fait.source || 'sans pièce'} · {dateLisible(fait.affirme_le)}</div>
                {fait.extrait && <blockquote className="mt-2 border-l-2 border-eg-bord pl-3 text-[12px] italic text-eg-brun">« {fait.extrait} »</blockquote>}
                {estAdmin && conflit.statut === 'ouvert' && (
                  <BoutonSombre className="mt-3" onClick={() => trancher(conflit, fait.id)}>
                    <Check className="h-3.5 w-3.5" /> Retenir cette valeur
                  </BoutonSombre>
                )}
              </div>
            ))}
          </div>
        </Carte>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------ Sources

const Sources: React.FC<{
  sources: SourceAutorite[];
  estAdmin: boolean;
  onChange: () => void;
  notifier: ReturnType<typeof useToast>;
}> = ({ sources, estAdmin, onChange, notifier }) => {
  const [ordre, setOrdre] = useState<SourceAutorite[]>(sources);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => setOrdre(sources), [sources]);

  const deplacer = (index: number, sens: -1 | 1) => {
    const suivant = [...ordre];
    const cible = index + sens;
    if (cible < 0 || cible >= suivant.length) return;
    [suivant[index], suivant[cible]] = [suivant[cible], suivant[index]];
    setOrdre(suivant);
  };
  const modifie = ordre.map((s) => s.type_source).join() !== sources.map((s) => s.type_source).join();

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const reponse = await memoireService.reordonner(ordre.map((s) => s.type_source));
      notifier.showInfo('Hiérarchie enregistrée', `${reponse.faits_rearbitres} fait(s) réarbitré(s).`);
      onChange();
    } catch (e: any) {
      notifier.showError('Enregistrement impossible', e.message);
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="max-w-[720px] space-y-4">
      <p className="text-[13px] leading-relaxed text-eg-brun">
        Quand deux sources se contredisent, celle qui est placée le plus haut l'emporte, sans
        déranger personne. Changer l'ordre réarbitre aussitôt toute la mémoire.
      </p>
      <Carte className="overflow-hidden">
        {ordre.map((source, index) => (
          <div key={source.type_source} className={`flex items-center gap-3 px-4 py-3 ${index > 0 ? 'border-t border-eg-bordure' : ''}`}>
            <span className="w-6 text-center font-mono text-[12px] text-eg-estompe">{index + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-medium text-eg-encre">{source.libelle}</div>
              <div className="text-[12px] text-eg-gris">{source.description}</div>
            </div>
            {estAdmin && (
              <div className="flex gap-1">
                <button type="button" onClick={() => deplacer(index, -1)} disabled={index === 0} className="rounded-full p-1.5 text-eg-gris hover:bg-black/[0.05] disabled:opacity-30" aria-label="Monter"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => deplacer(index, 1)} disabled={index === ordre.length - 1} className="rounded-full p-1.5 text-eg-gris hover:bg-black/[0.05] disabled:opacity-30" aria-label="Descendre"><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
        ))}
      </Carte>
      {estAdmin && modifie && (
        <div className="flex justify-end gap-2">
          <BoutonClair onClick={() => setOrdre(sources)}>Annuler</BoutonClair>
          <BoutonSombre onClick={enregistrer} disabled={envoi}>
            {envoi ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Enregistrer et réarbitrer
          </BoutonSombre>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ Page

const ONGLETS: OngletMemoire[] = ['apercu', 'documents', 'fiches', 'faits', 'conflits', 'sources', 'competences', 'modeles'];

/**
 * Mémoire du PEV : ce que la plateforme sait du programme, et de quelle source.
 *
 * Reprend le moteur de la mémoire d'entreprise : des documents qui font
 * autorité selon leur nature, des faits attribués à leur source et arbitrés
 * par une hiérarchie explicite, des fiches de synthèse versionnées, et les
 * seuls désaccords qu'aucune règle ne départage, soumis à un responsable.
 */
const MemoirePage: React.FC = () => {
  const { onglet: brut } = useParams<{ onglet?: string }>();
  const onglet: OngletMemoire = ONGLETS.includes(brut as OngletMemoire) ? (brut as OngletMemoire) : 'apercu';
  const notifier = useToast();
  const [apercu, setApercu] = useState<ApercuMemoire | null>(null);
  const estAdmin = authManager.hasRole('admin');

  const rafraichir = useCallback(() => {
    memoireService.apercu().then(setApercu).catch(() => {});
  }, []);
  useEffect(rafraichir, [rafraichir]);

  const sources = apercu?.sources || [];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1100px] pb-16">
        <NavMemoire actif={onglet} conflits={apercu?.conflits_ouverts} />

        {onglet === 'apercu' && <Apercu apercu={apercu} />}
        {onglet === 'documents' && <Documents sources={sources} estAdmin={estAdmin} onChange={rafraichir} notifier={notifier} />}
        {onglet === 'fiches' && <Fiches estAdmin={estAdmin} onChange={rafraichir} notifier={notifier} />}
        {onglet === 'faits' && <Faits estAdmin={estAdmin} onChange={rafraichir} notifier={notifier} />}
        {onglet === 'conflits' && <Conflits estAdmin={estAdmin} onChange={rafraichir} notifier={notifier} />}
        {onglet === 'sources' && <Sources sources={sources} estAdmin={estAdmin} onChange={rafraichir} notifier={notifier} />}
        {onglet === 'competences' && <Competences estAdmin={estAdmin} notifier={notifier} />}
        {onglet === 'modeles' && <Modeles estAdmin={estAdmin} notifier={notifier} />}
      </div>
      <ToastContainer toasts={notifier.toasts} onRemoveToast={notifier.removeToast} />
    </DashboardLayout>
  );
};

export default MemoirePage;
